import { ApiError } from "@khazzin/api-client";
import { describeApiError } from "@khazzin/api-client/errors";
import { ENGLISH, type Translate } from "@khazzin/i18n";

/**
 * What went wrong, in words a person can read (ADJ-M03-1).
 *
 * Screens wrote `t(\`inventory.errors.${error.code}\`)`, and a code nobody had
 * written a message for rendered as "…" in production. This tries the
 * screen's own message for the code first (`keyPrefix` + "." + code), then the
 * shared one for the code, then — for a refusal (4xx) — the API's own English
 * sentence, then the generic one — so there is always a sentence, and a
 * refusal never reads as a failure on our side. A network failure or a thrown Error that is not the API's gets the
 * generic sentence too: its `message` is English written for a developer.
 */
export function errorMessage(error: unknown, t: Translate, keyPrefix?: string): string {
	if (error instanceof ApiError) {
		const own = keyPrefix === undefined ? undefined : `${keyPrefix}.${error.code}`;
		if (own !== undefined && ENGLISH[own] !== undefined) {
			return t(own);
		}
		const shared = describeApiError(error.code);
		// A refusal nobody has written words for yet still says why, in the
		// API's own (English) sentence: a 4xx is the caller's to fix, and
		// "something went wrong on our side" would send them to the wrong door.
		if (
			shared === describeApiError("internal") &&
			error.code !== "internal" &&
			error.status < 500 &&
			error.message !== ""
		) {
			return error.message;
		}
		return t(shared);
	}
	return t("ui.state.errorTitle");
}
